import PropTypes from "prop-types"

/**
 * PropTypes for the MOBI.E console RequestResponsePanel component.
 *
 * @property {string} method - HTTP method of the OCPI call
 * @property {string} path - URL path, `{tokens}` stands for the hub tokens endpoint
 * @property {Object} [body] - OCPI body sent, absent on a GET
 * @property {Object|null} response - OCPI response returned by the hub (`status_code`, `status_message`, `data`)
 */
export const requestResponsePanelPropTypes = {
	method: PropTypes.string.isRequired,
	path: PropTypes.string.isRequired,
	body: PropTypes.object,
	response: PropTypes.object,
}

/**
 * PropTypes for the MOBI.E console OcpiLog component.
 *
 * @property {Object[]} entries - OCPI exchanges of this session, newest first
 * @property {Function} onClear - Empties the log
 */
export const ocpiLogPropTypes = {
	entries: PropTypes.arrayOf(PropTypes.object).isRequired,
	onClear: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console TokensTab component.
 *
 * @property {Object[]} tokens - Tokens stored for the operator
 * @property {Function} onSelect - Receives the documentId of the clicked row
 * @property {Function} onCreate - Opens the create modal
 */
export const tokensTabPropTypes = {
	tokens: PropTypes.arrayOf(PropTypes.object).isRequired,
	onSelect: PropTypes.func.isRequired,
	onCreate: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console TokenFormModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible
 * @property {Object|null} token - Token being edited, null when creating
 * @property {string|null} countryCode - Operator country_code, read-only
 * @property {string|null} partyId - Operator party_id, read-only
 * @property {boolean} busy - A hub call is running
 * @property {Function} onClose - Closes the modal
 * @property {Function} onSave - Receives the validated form values
 * @property {Function} onGenerateUid - Resolves with a free uid
 * @property {Function} onGenerateContractId - Resolves with a generated eMI3 contract id
 * @property {Function} onNotify - Shows a toast, `(title, appearance)`
 */
export const tokenFormModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	token: PropTypes.object,
	countryCode: PropTypes.string,
	partyId: PropTypes.string,
	busy: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	onGenerateUid: PropTypes.func.isRequired,
	onGenerateContractId: PropTypes.func.isRequired,
	onNotify: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console CommandsTab component.
 *
 * @property {Object[]} commands - Commands sent in this session, newest first
 * @property {Function} onSelect - Receives the command_id of the clicked row
 */
export const commandsTabPropTypes = {
	commands: PropTypes.arrayOf(PropTypes.object).isRequired,
	onSelect: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console StartSessionModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible
 * @property {Object[]} tokens - Tokens stored for the operator
 * @property {Object[]} locations - Locations with their EVSEs
 * @property {boolean} busy - A hub call is running
 * @property {Function} onClose - Closes the modal
 * @property {Function} onSave - Receives the validated form values
 */
export const consoleStartSessionModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	tokens: PropTypes.arrayOf(PropTypes.object).isRequired,
	locations: PropTypes.arrayOf(PropTypes.object).isRequired,
	busy: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console StopSessionModal component.
 *
 * @property {boolean} isOpen - Whether the modal is visible
 * @property {Object[]} sessions - Active sessions of the operator
 * @property {boolean} busy - A hub call is running
 * @property {Function} onClose - Closes the modal
 * @property {Function} onSave - Receives the session id
 */
export const consoleStopSessionModalPropTypes = {
	isOpen: PropTypes.bool.isRequired,
	sessions: PropTypes.arrayOf(PropTypes.object).isRequired,
	busy: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console GetsTab component.
 *
 * @property {Object} hubReads - Result of `useHubReads`: results, lastFullRead, busy, formOf, change and read
 */
export const getsTabPropTypes = {
	hubReads: PropTypes.shape({
		results: PropTypes.object.isRequired,
		lastFullRead: PropTypes.number,
		busy: PropTypes.bool,
		formOf: PropTypes.func.isRequired,
		change: PropTypes.func.isRequired,
		read: PropTypes.func.isRequired,
	}).isRequired,
}

/**
 * PropTypes for the MOBI.E console HubReadCard component.
 *
 * @property {string} module - Hub module the card reads, `locations` adds the id field
 * @property {string} label - Translation key of the card title
 * @property {Object} form - Values of the card form
 * @property {Object} [result] - Last read, `{ method, path, body, response }`
 * @property {boolean} busy - A hub call is running
 * @property {number} waitSeconds - Seconds left before a full locations read is allowed, 0 when allowed
 * @property {Function} onChange - Receives the field name and its new value
 * @property {Function} onRead - Reads the module from the hub
 */
export const hubReadCardPropTypes = {
	module: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	form: PropTypes.object.isRequired,
	result: PropTypes.object,
	busy: PropTypes.bool,
	waitSeconds: PropTypes.number.isRequired,
	onChange: PropTypes.func.isRequired,
	onRead: PropTypes.func.isRequired,
}

/**
 * PropTypes for the MOBI.E console ConsoleSidebar component.
 *
 * @property {string} [selectionKey] - Changes when a row is selected, switches the sidebar to Details
 * @property {React.ReactNode} details - Content of the Details tab
 * @property {React.ReactNode} actions - Content of the Actions tab
 */
export const consoleSidebarPropTypes = {
	selectionKey: PropTypes.string,
	details: PropTypes.node,
	actions: PropTypes.node,
}

/**
 * PropTypes for MobieConsole view.
 * No external props are expected; kept for consistency across view definitions.
 */
export const mobieConsoleViewPropTypes = {}
